import { useMemo, useState } from 'react';
import cm from 'classnames';

import { Project, ProjectStatus } from 'services/project.service';
import { ObjectType } from 'services/access.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { prettyBytes, prettyDate } from 'components/app/_helpers/_app.helper';
import {
  fetchProjects,
  createProject,
  updateEntity,
  setRemovingInstance,
  selectProject,
} from 'components/app/_mediator/app-data.slice';
import { ModalEnum, setModal } from 'components/app/_mediator/app.slice';
import { sortProjectEntities } from '../_mediator/projects.helper';
import { setAccessTypeModal, setSelectedProject } from '../_mediator/projects.slice';
import { getProjectsSortCb, PROJECTS_LIST_HEADERS } from '../_mediator/projects.params';
import { useProjectsSearch } from '../use-projects-search';

import { ReactComponent as MoveToIcon } from 'assets/ui/move-to.svg';
import { ReactComponent as ShareIcon } from 'assets/ui/share.svg';
import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';
import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';

import { EditCell } from 'components/app/app-components/edit-cell/edit-cell';
import { PlaceholderProjects } from '../placeholders/placeholder-projects';

import { CreateElementModal } from '../elements/modal-create-element';
import { ShareProjectModal } from '../elements/modal-share-project';
import { HeaderAccessIco } from '../elements/header-access-ico';

import { ProjectsTable } from './elements/projects-table';
import { Section } from './elements/section';
import { SectionHeader } from './elements/section-header';
import { HeaderAction } from '../elements/header-action';
import { HeaderSearch } from '../elements/header-search';
import { HeaderActionFolder } from '../elements/header-action-folders';

import styles from './_sections.module.scss';

export const SectionProjects = () => {
  const { selectedProject, projectsTableState, projectsTableSort } = useAppSelector((state) => state.projects);
  const { projects, removingInstance } = useAppSelector((state) => state.app__data);
  const projectData = useAppSelector(selectProject);
  const modal = useAppSelector((state) => state.app.modal);
  const dispatch = useAppDispatch();

  const { searchValue, updateSearchValue, getFilteredList, clearSearchValue } =
    useProjectsSearch('projectsSearchFilter');
  const [projectName, setProjectName] = useState('');

  const isDisabled = useMemo(() => {
    return !selectedProject || removingInstance?.instance !== 'project';
  }, [selectedProject, removingInstance?.instance]);

  const filteredProjects = useMemo(() => (projects ? getFilteredList<Project>(projects) : []), [projects, searchValue]);

  const sortedProjects = useMemo(() => {
    return sortProjectEntities(
      filteredProjects,
      projectsTableSort.order,
      getProjectsSortCb(PROJECTS_LIST_HEADERS[projectsTableSort.column])
    );
  }, [filteredProjects, projectsTableSort]);

  const handleSelect = (projectId: number) => {
    dispatch(setRemovingInstance({ id: projectId, instance: 'project', modalOpen: false }));
    dispatch(setSelectedProject(projectId));
  };

  const handleCreate = async () => {
    await dispatch(createProject(projectName)).unwrap();
    await dispatch(fetchProjects())
      .unwrap()
      .then((projects) => {
        const createdProject = projects.find((el) => el.name === projectName);
        if (createdProject) dispatch(setSelectedProject(createdProject.projectId));
        else console.error('project not found');
      });

    clearSearchValue(projectName);
    setProjectName('');
    dispatch(setRemovingInstance(null));
  };

  return (
    <Section expanded={projectsTableState}>
      <SectionHeader
        expanded={projectsTableState}
        keyTableState="projectsTableState"
        title="Projects"
        empty={!projects?.length}
        len={sortedProjects.length}
      >
        <HeaderSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          clearSearchValue={clearSearchValue}
        />
        <HeaderAction
          type="create"
          icon={<PlusIcon />}
          onSubmit={() => dispatch(setModal(ModalEnum.addProject))}
          data-intro={IntroKeys.createProject}
          disabled={false}
          tip="Add a project"
        />
        <HeaderAction
          type="share"
          icon={<ShareIcon />}
          onSubmit={() => dispatch(setModal(ModalEnum.projectShare))}
          disabled={!selectedProject}
          tip="Manage project collaborators"
        />
        <HeaderAction
          type="access"
          icon={<HeaderAccessIco accessType={projectData?.accessType} />}
          onSubmit={() => dispatch(setAccessTypeModal(ObjectType.Project))}
          disabled={!selectedProject}
          tip="Manage project access"
        />
        <HeaderAction
          type="delete"
          icon={<TrashIcon />}
          onSubmit={() => {
            removingInstance?.instance === 'project' &&
              dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          }}
          disabled={isDisabled}
          tip={isDisabled ? 'Select a project for deletion' : 'Delete project'}
        />
        <HeaderActionFolder type="project" disabled={isDisabled}>
          <HeaderAction type="moveTo" icon={<MoveToIcon />} disabled={isDisabled} />
        </HeaderActionFolder>
      </SectionHeader>

      <div className={styles.SectionBody}>
        <PlaceholderProjects cb={() => dispatch(setModal(ModalEnum.addProject))} />
        {projects && projects.length !== 0 && (
          <ProjectsTable
            sortKey="projectsTableSort"
            headers={PROJECTS_LIST_HEADERS}
            selectedRow={selectedProject ?? undefined}
          >
            {sortedProjects?.map((row) => (
              <tr
                key={row.projectId}
                onClick={() => handleSelect(row.projectId)}
                className={cm({
                  [styles.selected]: row.projectId === selectedProject,
                  [styles.removing]:
                    removingInstance?.instance === 'project' &&
                    removingInstance?.modalOpen &&
                    removingInstance?.id === row.projectId,
                })}
              >
                <EditCell
                  element={{ name: row.name, id: row.projectId }}
                  onSubmit={(element) =>
                    dispatch(updateEntity({ id: element.id, name: element.name, entity: 'projects' }))
                  }
                  selectedID={selectedProject}
                  selectedName={projectData?.name ?? ''}
                />
                <td>{ProjectStatus[row.status]}</td>
                <td>{prettyBytes(row.size)}</td>
                <td>{prettyDate(row.createdAt)}</td>
                <td>{prettyDate(row.accessedAt)}</td>
                <td>{prettyDate(row.updatedAt)}</td>
              </tr>
            ))}
          </ProjectsTable>
        )}
      </div>

      {/* modals */}

      <ShareProjectModal isOpen={modal === ModalEnum.projectShare} />

      <CreateElementModal
        isOpen={modal === ModalEnum.addProject}
        onClose={() => {
          dispatch(setModal(0));
          setProjectName('');
        }}
        onSubmit={() => {
          dispatch(setModal(0));
          handleCreate();
        }}
        modalTitle="Name your project"
        inputValue={projectName}
        onInputChange={(value) => setProjectName(value)}
        disabled={!projectName}
      />
    </Section>
  );
};
